import { useMissionsUrl } from '@/features/missions/context/MissionsUrlProvider'
import { routes } from '@/pages'
import { Button, Text } from '@/ui-kit'
import { formatNumber } from '@/utils'
import { type MessageDescriptor } from '@lingui/core'
import { msg, plural } from '@lingui/core/macro'
import { Trans, useLingui } from '@lingui/react/macro'
import { useNavigate } from 'react-router-dom'
import type { NectarEnrollmentStatus, NectarProject } from '../api/models'
import { isProlificMode } from '../prolific/session'

export type TerminalStatus = Exclude<NectarEnrollmentStatus, 'IN_PROGRESS' | 'PENDING_REVIEW'>

const STATUS_CONFIG: Record<
  TerminalStatus,
  {
    title: MessageDescriptor
    description: string
    showReward: boolean
    rewardPrefix: MessageDescriptor | null
  }
> = {
  APPROVED: {
    title: msg`Mission Completed`,
    description: 'Your submission has been approved. Great work!',
    showReward: true,
    rewardPrefix: msg`Earned`
  },
  REJECTED: {
    title: msg`Submission Rejected`,
    description: 'Unfortunately, your submission did not meet the requirements.',
    showReward: false,
    rewardPrefix: null
  },
  EXPIRED: {
    title: msg`Mission Expired`,
    description: 'This project has expired and is no longer accepting submissions.',
    showReward: false,
    rewardPrefix: null
  }
}

export const NectarProjectStatusView = ({ project, status }: { project: NectarProject; status: TerminalStatus }) => {
  const { t } = useLingui()
  const navigate = useNavigate()
  const { missionsUrl } = useMissionsUrl()
  const prolificMode = isProlificMode()
  const config = STATUS_CONFIG[status]
  const formatReward = (amount: number) =>
    project.rewardConfig?.type === 'usd'
      ? `$${formatNumber(amount, 2)}`
      : plural(amount, { one: '# point', other: '# points' })
  const earnedReward = formatReward(project.enrollment?.earnedReward ?? 0)
  const rewardAmount = project.rewardConfig?.amount || 0

  return (
    <>
      <div className="max-mobile:mt-6 m-auto mt-10 flex w-full max-w-[600px] flex-col items-center justify-center gap-4 rounded-2xl border border-solid border-gray-100 px-10 py-12 text-center">
        <Text type="h5" className="text-gray-70">
          {t(config.title)}
        </Text>
        <div className="flex flex-col items-center justify-center">
          <Text type="body1-regular" className="text-gray-80">
            {config.description}
          </Text>
          {config.showReward &&
            (!!project.totalReward || !!project.rewardConfig?.amount) &&
            (status === 'APPROVED' ? (
              project.enrollment?.earnedReward != null && (
                <Text type="body1-regular">
                  <Trans>
                    Earned{' '}
                    <span className={project.rewardConfig?.type === 'usd' ? 'text-yellow' : 'text-green'}>
                      {earnedReward}
                    </span>
                    !
                  </Trans>
                </Text>
              )
            ) : (
              <Text type="body1-regular" className="text-green">
                {config.rewardPrefix ? t(config.rewardPrefix) : ''}{' '}
                <span className={project.rewardConfig?.type === 'usd' ? 'text-yellow' : 'text-green'}>
                  {project.rewardConfig?.type === 'usd'
                    ? `$${formatNumber(project.totalReward || 0, 2)}`
                    : plural(rewardAmount, { one: '# point', other: '# points' })}
                </span>
                .
              </Text>
            ))}
        </div>
        <div className="text-body-3 text-gray-80 mt-8">
          <Trans>Keep an eye on your missions activity or continue exploring other missions.</Trans>
        </div>

        {!prolificMode && (
          <div className="flex flex-col items-center justify-center gap-2">
            <div className="flex gap-2">
              <Button variant="bordered" size="small" onClick={() => navigate(routes.activity.path)}>
                <Trans>View Activity</Trans>
              </Button>
              <Button size="small" onClick={() => navigate(missionsUrl)}>
                <Trans>Back to Missions</Trans>
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  )
}
